import React, { CSSProperties } from 'react';
import { Box, useTheme } from '@material-ui/core';
import Schedule from '@material-ui/icons/Schedule';
import { HoursOfOperationsType } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import moment from 'moment';
import { LocationCore, MobileAddress } from 'types/UFOTypes';
import { colorPalette } from 'utils/colorsPalette';
import { formatPhone } from 'utils/format';
import { isHoursOfOperations } from './LocationInformation/utils/isHoursOfOperations';

const boxStyle: CSSProperties = { overflow: 'hidden' };
const iconStyle: CSSProperties = {
  marginTop: -5,
  marginRight: 16,
  float: 'left',
};

export default function LocationInformation({
  mobileAddress,
  isMobile,
  location,
  printMode = false,
}: {
  location: LocationCore;
  isMobile?: boolean;
  printMode?: boolean;
  mobileAddress?: MobileAddress;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  let isOpen = false;
  if (isHoursOfOperations(location?.hours_of_operation)) {
    const hoursOfOperations = location?.hours_of_operation;
    const locationTimeZone = location?.timeZone || '-0400';

    const locationWeekDay = moment()
      .utcOffset(locationTimeZone)
      .format('dddd')
      .toLowerCase() as keyof HoursOfOperationsType;

    if (hoursOfOperations[locationWeekDay]?.begin && hoursOfOperations[locationWeekDay]?.end) {
      const locationTime = moment().utcOffset(locationTimeZone).format('HH:mm');
      const open = moment(hoursOfOperations[locationWeekDay]?.begin, 'HH:mm');
      const close = moment(hoursOfOperations[locationWeekDay]?.end, 'HH:mm');
      isOpen = moment(locationTime, 'HH:mm').isBetween(open, close, 'minute');
    }
  }
  if (printMode) {
    return (
      <span style={{ fontSize: 12 }}>
        {location?.customer_name}
        <br />
        {location?.address}
        <br />
        {`${location?.customer_city ?? ''} ${location?.customer_state ?? ''} ${location?.customer_zip ?? ''}`}
      </span>
    );
  }

  return (
    <>
      {location?.customer_number && (
        <Box style={{ minWidth: 150 }}>
          <span style={{ fontSize: 12 }}>
            <Box style={boxStyle}>
              <span style={{ color: colorPalette.gray, fontSize: 'small' }}>{t('Name')}</span>
              <br />
              {location?.customer_dba_name || location?.customer_name}
            </Box>
            {!isMobile && (
              <Box style={boxStyle}>
                <span style={{ color: colorPalette.gray, fontSize: 'small' }}>{t('Address')}</span>
                <br />
                {location?.address}
                <br />
                {`${location?.customer_city ?? ''} ${location?.customer_state ?? ''} ${location?.customer_zip ?? ''}`}
              </Box>
            )}
            {mobileAddress && isMobile && (
              <Box style={boxStyle}>
                <span style={{ color: colorPalette.gray, fontSize: 'small' }}>{t('Installation Location')}</span>
                <br />
                {`${mobileAddress?.streetNumber || ''} ${mobileAddress?.street || mobileAddress?.address || ''}`}
                <br />
                {`${mobileAddress.city || ''}, ${mobileAddress.state || ''}, ${mobileAddress.zip || ''}`}
              </Box>
            )}
            {location?.phone_number && (
              <Box style={boxStyle}>
                <span
                  style={{
                    color: colorPalette.gray,
                    fontSize: 'small',
                  }}
                >
                  {t('Phone Number')}
                </span>
                <br />
                {`${formatPhone(location?.phone_number)}`}
              </Box>
            )}
            <Box style={boxStyle}>
              <span
                style={{
                  color: colorPalette.gray,
                  fontSize: 'small',
                }}
              >
                {location?.customer_type === 'ST' && (
                  <>
                    {t('Ship to #')}
                    {location?.customer_number}
                  </>
                )}
                {location?.customer_type === 'BT' && (
                  <>
                    {t('Bill to #')}
                    {location?.customer_number}
                  </>
                )}
              </span>
            </Box>
            {location?.hours_of_operation && (
              <>
                <br />
                <Schedule style={iconStyle} />
                <Box style={boxStyle}>
                  <span style={{ color: isOpen ? theme.palette.success.main : theme.palette.error.main }}>
                    {isOpen ? t('Open') : t('Closed')}
                  </span>
                </Box>
              </>
            )}
          </span>
        </Box>
      )}
    </>
  );
}
